<template>
  <div class="p-1 pr-0">
    <div v-if="items.length > 3 || first_node" class="mb-2">
      <div class="relative rounded-md shadow-sm">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
          <i class="text-sm fa fa-search"></i>
        </div>
        <input type="text" v-model="search" placeholder="Kërko..." class="block w-full py-2 pr-3 text-sm border-gray-300 rounded-md pl-9 focus:outline-none focus:ring-2 focus:ring-blue-400" />
      </div>
    </div>

    <ul class="list-none p-0 m-0 space-y-1 max-h-[calc(100vh-250px)] overflow-y-auto text-sm">
      <li v-for="i in filteredItems" :key="i.guid" class="relative p-0 m-0 tree-item">
        <div class="transition-colors duration-200 rounded-md group hover:bg-gray-50">
          <div class="flex items-center">
            <button
              v-if="hasChildren(i)"
              type="button"
              @click.prevent="toggleCollapse(i)"
              class="flex items-center justify-center w-5 h-5 text-gray-500 hover:text-gray-700 focus:outline-none"
              :aria-expanded="!i.collapsed"
              :aria-label="i.collapsed ? 'Hap kategorinë' : 'Mbyll kategorinë'"
            >
              <i class="text-xs fa" :class="[i.collapsed ? 'fa-plus-square' : 'fa-minus-square']"></i>
            </button>
            <i v-else class="w-5 h-4 text-gray-400 far fa-circle"></i>

            <div class="flex-1 flex items-center ml-1 min-h-[24px] rounded-md" :class="rowClass(i)">
              <div class="flex items-center justify-between flex-1 gap-1">
                <button
                  v-if="hasChildren(i)"
                  type="button"
                  @click.prevent="toggleCollapse(i)"
                  class="flex-1 min-w-0 py-0.5 text-left truncate cursor-pointer focus:outline-none"
                  :class="isInactive(i) ? 'hover:text-gray-600' : 'hover:text-gray-900'"
                  :title="itemTitle(i)"
                >
                  {{ i.name }}
                </button>
                <div v-else class="flex-1 min-w-0 truncate" :title="itemTitle(i)">
                  {{ i.name }}
                </div>

                <div class="flex items-center gap-1 pr-1 flex-nowrap" @click.stop>
                  <span v-if="hasChildren(i)" class="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800" title="Nënkategori">
                    {{ i.children.length }}
                  </span>

                  <span v-if="i.total > 0" class="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800" :title="module?.title + ' të lidhura'">
                    {{ i.total }}
                  </span>

                  <img v-if="i.image" :src="i.image" :alt="i.name" class="object-cover w-6 h-6 rounded-full" />

                  <div class="flex items-center space-x-1 transition-opacity duration-200 opacity-0 group-hover:opacity-100">
                    <button v-if="showAddButton(i)" @click.prevent="handleAction(i, 'add')" class="p-1 text-gray-400 rounded-full hover:text-green-600 focus:outline-none hover:bg-gray-100" title="Shto Nën-kategori">
                      <i class="text-xs fa fa-plus"></i>
                    </button>
                    <button @click.prevent="handleAction(i, 'edit')" class="p-1 text-gray-400 rounded-full hover:text-blue-600 focus:outline-none hover:bg-gray-100" title="Edito">
                      <i class="text-xs fa fa-pencil"></i>
                    </button>
                    <button
                      v-if="!hasChildren(i) && ['products', 'accessories', 'warehouses'].includes(related_entity)"
                      @click.prevent="handleAction(i, 'create')"
                      class="p-1.5 text-indigo-500 rounded-full hover:text-indigo-700 focus:outline-none hover:bg-indigo-50"
                      :title="createEntityTitle"
                    >
                      <i class="text-base fa fa-box"></i>
                    </button>
                    <button
                      v-if="['products', 'accessories'].includes(related_entity)"
                      @click.prevent="openLabelExportModal(i)"
                      class="p-1 text-gray-400 rounded-full hover:text-teal-600 focus:outline-none hover:bg-gray-100"
                      title="Eksporto etiketat"
                    >
                      <i class="text-xs fa fa-tags"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Transition name="slide-fade">
            <Tree v-if="shouldShowChildren(i)" :items="i.children" :flat="flat" :levels="levels" :module="module" :related_entity="related_entity" :first_node="false" class="ml-4 mt-0.5" />
          </Transition>
        </div>
      </li>

      <li v-if="filteredItems.length === 0" class="py-2 text-sm text-center text-gray-500">Nuk ka rezultate për "{{ search }}"</li>
    </ul>

    <LabelExportModal
      :open="labelExportModal.open"
      :loading="labelExportLoading"
      :export-context="labelExportContext"
      :mode="labelExportModal.mode"
      :notice-product-count="labelExportModal.noticeCount"
      title="Eksporto etiketat (PDF)"
      :subtitle="labelExportModal.category && labelExportModal.mode === 'export' ? ('Kategoria: <strong>' + labelExportModal.category.name + '</strong>') : ''"
      @close="closeLabelExportModal"
      @confirm="confirmLabelExport"
    />
  </div>
</template>

<script>
import { storeToRefs } from "pinia";
import { useCategoryStore } from "@/stores/category";
import { useAccessoryStore } from "@/stores/accessory";
import CategoriesServices from "@/services/Category";
import AccessoryServices from "@/services/Accessory";
import ProductsServices from "@/services/Product";
import LabelExportModal from "@/components/LabelExportModal.vue";

export default {
  name: "Tree",
  components: { LabelExportModal },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    flat: Boolean,
    levels: {
      type: Number,
      default: -1,
    },
    node_has_children: {
      type: Object,
      default: null,
    },
    module: {
      type: Object,
      default: null,
    },
    first_node: {
      type: Boolean,
      default: false,
    },
    related_entity: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      search: "",
      labelExportLoading: false,
      labelExportModal: {
        open: false,
        category: null,
        mode: "export",
        noticeCount: 1,
      },
      categories: storeToRefs(useCategoryStore()),
      accessories: storeToRefs(useAccessoryStore()),
    };
  },
  computed: {
    filteredItems() {
      return this.items.filter((item) => JSON.stringify(item).toLowerCase().includes(this.search.toLowerCase()));
    },
    list_items() {
      if (this.flat) {
        return this.toTree(this.items);
      }
      return this.items;
    },
    labelExportContext() {
      const cat = this.labelExportModal.category;
      return {
        product_guids: [],
        category_guid: cat?.guid || null,
        related_entity: this.related_entity || "products",
      };
    },
    createEntityTitle() {
      if (this.related_entity === "warehouses") return "Shto Magazinë";
      if (this.related_entity === "accessories") return "Shto Aksesor Këtu";
      return "Shto Produkt Këtu";
    },
  },
  methods: {
    hasChildren(item) {
      return Array.isArray(item.children) && item.children.length > 0;
    },

    isCurrentItem(item) {
      return item.guid === this.categories.current?.guid || item.guid === this.accessories.current?.guid;
    },

    isInactive(item) {
      return item.active === 0 || item.active === "0" || item.active === false;
    },

    rowClass(item) {
      const current = this.isCurrentItem(item);
      const inactive = this.isInactive(item);
      if (inactive && current) return "bg-gray-100 text-gray-500 italic font-semibold";
      if (inactive) return "text-gray-400 italic";
      if (current) return "bg-blue-50 text-blue-800 font-semibold";
      return "";
    },

    itemTitle(item) {
      if (this.isInactive(item)) return item.name + " (jo aktive)";
      if (this.hasChildren(item)) return item.collapsed ? "Hap kategorinë" : "Mbyll kategorinë";
      return item.name;
    },

    showAddButton(item) {
      return this.levels === -1 || (this.levels > 1 && this.node_has_children?.prop && this.node_has_children?.value && item[this.node_has_children.prop] === this.node_has_children.value);
    },

    shouldShowChildren(item) {
      return this.hasChildren(item) && !item.collapsed;
    },

    async toggleCollapse(item) {
      item.collapsed = !item.collapsed;
      await this.updateStores(item);
    },

    async updateStores(item) {
      // Update categories store
      this.categories.flat_list = this.categories.flat_list.map((e) => (e.guid === item.guid ? { ...e, collapsed: item.collapsed } : e));
      this.categories.list = await CategoriesServices.getTreeCategories(this.categories.flat_list, "id");

      // Update accessories store
      this.accessories.flat_list = this.accessories.flat_list.map((aa) => (aa.guid === item.guid ? { ...aa, collapsed: aa.collapsed } : aa));
      this.accessories.list = await AccessoryServices.getTreeAccessories(this.accessories.flat_list, "id");
    },

    handleAction(item, mode) {
      this.categories.mode = mode;
      this.accessories.mode = mode;
      this.categories.current = { ...item };
      this.accessories.current = { ...item };
    },

    openLabelExportModal(item) {
      if (!item?.guid) return;
      this.labelExportLoading = true;
      ProductsServices.previewLabelExport({
        category_guid: item.guid,
        related_entity: this.related_entity || "products",
        include_descendants: true,
      })
        .then((data) => {
          if (!data?.total_labels) {
            this.labelExportModal = {
              open: true,
              category: item,
              mode: "notice",
              noticeCount: Math.max(1, data?.total_products || 2),
            };
            return;
          }
          this.labelExportModal = {
            open: true,
            category: item,
            mode: "export",
            noticeCount: Math.max(1, data.total_products || 1),
          };
        })
        .catch((e) => {
          const msg = e?.response?.data?.message || e?.message || "Gabim gjatë ngarkimit të preview.";
          if (this.$toast) this.$toast.error(msg);
        })
        .finally(() => {
          this.labelExportLoading = false;
        });
    },

    closeLabelExportModal() {
      this.labelExportModal = {
        open: false,
        category: null,
        mode: "export",
        noticeCount: 1,
      };
    },

    async confirmLabelExport({ per_page }) {
      const category = this.labelExportModal.category;
      if (!category?.guid) return;
      this.labelExportLoading = true;
      try {
        await ProductsServices.exportLabels({
          category_guid: category.guid,
          related_entity: this.related_entity || "products",
          include_descendants: true,
          per_page,
        });
        if (this.$toast) this.$toast.success("PDF i etiketave u shkarkua.");
        this.closeLabelExportModal();
      } catch (e) {
        if (this.$toast) this.$toast.error(e?.message || "Nuk u shkarkua PDF i etiketave.");
      } finally {
        this.labelExportLoading = false;
      }
    },

    toTree(dataset) {
      const hashTable = Object.create(null);
      dataset.forEach((aData) => {
        hashTable[aData.guid] = { ...aData, children: [] };
      });

      const dataTree = [];
      dataset.forEach((aData) => {
        if (aData.parent_id && hashTable[aData.parent_id]) {
          hashTable[aData.parent_id].children.push(hashTable[aData.guid]);
        } else {
          dataTree.push(hashTable[aData.guid]);
        }
      });
      return dataTree;
    },
  },
};
</script>

<style scoped>
.tree-item::before {
  content: "";
  position: absolute;
  width: 1px;
  height: calc(100% - 0.5rem);
  background-color: #d1d5db;
  left: -0.75rem;
  top: 1rem;
}

.tree-item::after {
  content: "";
  position: absolute;
  width: 0.75rem;
  height: 1px;
  background-color: #d1d5db;
  left: -0.75rem;
  top: 1.5rem;
}

.tree-item:last-child::before {
  display: none;
}

.slide-fade-enter-active {
  transition: all 0.15s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.15s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-0.25rem);
  opacity: 0;
}

ul::-webkit-scrollbar {
  width: 3px;
}

ul::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 2px;
}

ul::-webkit-scrollbar-track {
  background-color: #f3f4f6;
}
</style>
